<template>
	<DataDialog ref="dialog" 
		width="80%" 
		height="700" 
		:title="$t('text.cancellationJobStatus')"
		v-model="model"
		:confirmLabel="confirmLabel"
		:cancelLabel="$t('text.close')"
		:onConfirm="handleConfirm"
		:disableConfirmButton="!selectedJob"
	>
		<template #content="{ model }">
			<div style="padding: 24px;">
				<div class="job-header-section">
					<h3 style="margin: 0 0 8px 0;">Cancellation Job</h3>
					<p style="margin: 0; color: #666; font-size: 14px;">
						Job for {{ formatDate(selectedDay?.date) }} {{ selectedSlot?.time || 'All Day' }}
					</p>
				</div>

				<div v-if="loading" class="loading-section">
					<v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
					<p style="margin: 16px 0 0 0; color: #666;">{{ $t('text.loadingJob') }}</p>
				</div>

				<div v-else-if="!job" class="no-job">
					<v-icon size="48" color="grey">mdi-briefcase-remove</v-icon>
					<p style="margin: 16px 0 0 0; color: #666;">{{ $t('text.noCancellationJobFound') }}</p>
					<p style="margin: 8px 0 0 0; color: #666; font-size: 12px;">
						{{ $t('text.createNewJobHint') }}
					</p>
				</div>

				<div v-else class="job-display">
					<div class="job-card" :class="{ 'running': isJobRunning(job), 'has-errors': hasFailedItems(job) }">
						<div class="job-header">
							<div class="job-info">
								<div class="job-title">{{ job.name }}</div>
								<div class="job-meta">
									<span class="job-id">Job ID: {{ job.jobId.substring(0, 8) }}...</span>
									<span class="job-date">Created: {{ formatJobDate(job.createdAt) }}</span>
								</div>
							</div>
							<div class="job-status">
								<v-chip 
									:color="getJobStatusColor(job.status)"
									:variant="job.status === 'NEW' ? 'outlined' : 'flat'"
									size="default"
								>
									<v-icon 
										v-if="isJobRunning(job)" 
										size="16" 
										style="margin-right: 4px;"
									>
										mdi-loading mdi-spin
									</v-icon>
									{{ getJobStatusLabel(job.status) }}
								</v-chip>
							</div>
						</div>

						<div v-if="job.statusData" class="job-details">
							<div class="job-progress" v-if="job.statusData.progress !== undefined">
								<div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
									<span style="font-weight: 500;">{{ $t('text.progress') }}</span>
									<span style="font-weight: 500; color: #1976d2;">
										{{ job.statusData.progress || 0 }}%
									</span>
								</div>
								<v-progress-linear
									:model-value="job.statusData.progress || 0"
									color="primary"
									height="8"
									rounded
								></v-progress-linear>
							</div>

							<div class="job-summary" v-if="job.statusData.summary">
								<h4 style="margin: 16px 0 12px 0; font-size: 16px;">{{ $t('text.summary') }}</h4>
								<div class="summary-grid">
									<div class="summary-card">
										<div class="summary-number">{{ job.statusData.summary.totalOrderItems || 0 }}</div>
										<div class="summary-label">{{ $t('text.totalOrderItems') }}</div>
									</div>
									<div class="summary-card success">
										<div class="summary-number">{{ job.statusData.summary.successfulCancellations || 0 }}</div>
										<div class="summary-label">{{ $t('text.successful') }}</div>
									</div>
									<div class="summary-card error">
										<div class="summary-number">{{ job.statusData.summary.failedCancellations || 0 }}</div>
										<div class="summary-label">{{ $t('text.failed') }}</div>
									</div>
									<div class="summary-card" v-if="job.statusData.summary.successRate !== undefined">
										<div class="summary-number">{{ job.statusData.summary.successRate }}%</div>
										<div class="summary-label">{{ $t('text.successRate') }}</div>
									</div>
								</div>
							</div>

							<div class="job-message" v-if="job.statusData.message">
								<p style="margin: 16px 0 0 0; padding: 12px; background: #f5f5f5; border-radius: 4px; font-style: italic;">
									{{ job.statusData.message[selectedLocale] }}
								</p>
							</div>

							<!-- Restart section for failed jobs -->
							<div v-if="hasNonCancelledItems && hasFailedItems(job) && !isJobRunning(job)" class="restart-section">
								<v-alert type="warning" variant="tonal" style="margin: 20px 0;">
									<div style="font-weight: 500; margin-bottom: 8px;">
										{{ job.statusData.summary?.failedCancellations || 0 }} {{ $t('text.orderItemsFailedToCancel') }}
									</div>
									<div style="font-size: 14px; margin-bottom: 12px;">
										{{ $t('text.youCanRestartTheJobToRetryOnlyTheFailedItems') }}
									</div>
									<v-btn 
										color="warning" 
										variant="outlined" 
										size="small"
										@click="restartJob"
									>
										<v-icon left size="16">mdi-restart</v-icon>
										{{ $t('text.restartJobFailedItemsOnly') }}
									</v-btn>
								</v-alert>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</DataDialog>
</template>

<script>
import DataDialog from '../../common/DataDialog.vue'
import Common from '@/mixins/Common.vue'
import BackgroundJobExecutive from '../../../../../api2/src/modules/jobs/BackgroundJobExecutive'

export default {
	components: { DataDialog },
	mixins: [Common],
	props: {
		product: Object,
		selectedDay: Object,
		selectedSlot: Object,
		orderItems: Array,
	},
	data() {
		return {
			model: {},
			job: null,
			loading: false,
			backgroundJobExecutive: new BackgroundJobExecutive(this),
			pollInterval: null,
		}
	},
	computed: {
		confirmLabel() {
			if (!this.job) return null
			if (this.isJobRunning(this.job)) {
				return this.$t('text.monitorJob')
			}
			return this.$t('text.viewDetails')
		},
		selectedJob() {
			// For compatibility with existing code
			return this.job
		},
		hasNonCancelledItems() {
			// Check if there are any order items that are not yet cancelled
			return this.orderItems?.length > 0 && this.orderItems.some(item => item.status?.refunded === 0)
		}
	},
	methods: {
		async open(job) {
			this.model = {}
			
			this.job = job
			console.log('Opening dialog with provided job:', this.job)

			this.$refs.dialog.open()
		},

		async handleConfirm() {
			if (!this.job) return false

			// Always emit monitor-job event to show detailed view in cancellation dialog
			this.$emit('monitor-job', this.job)

			return true // Close dialog
		},

		async restartJob() {
			if (!this.job || !this.hasFailedItems(this.job)) return

			try {
				// Emit restart event to parent component
				this.$emit('restart-job', this.job)
				// Close this dialog
				this.$refs.dialog.cancel()
			} catch (error) {
				console.error('Failed to restart job:', error)
				this.showError(this.$t('text.failedToRestartJob'))
			}
		},

		hasFailedItems(job) {
			return job?.statusData?.summary?.failedCancellations > 0
		},

		isJobRunning(job) {
			return job.status === 'NEW' || job.status === 'RUNNING'
		},

		getJobStatusColor(status) {
			switch (status) {
				case 'NEW': return 'blue'
				case 'RUNNING': return 'orange'
				case 'DONE': return 'green'
				case 'ERROR': return 'red'
				default: return 'grey'
			}
		},

		getJobStatusLabel(status) {
			switch (status) {
				case 'NEW': return this.$t('text.jobStatusPending')
				case 'RUNNING': return this.$t('text.jobStatusRunning')
				case 'DONE': return this.$t('text.jobStatusCompleted')
				case 'ERROR': return this.$t('text.jobStatusFailed')
				default: return status
			}
		},

		formatDate(dateString) {
			if (!dateString) return ''
			return new Date(dateString).toLocaleDateString('en-US', {
				weekday: 'short',
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			})
		},

		formatJobDate(dateString) {
			if (!dateString) return ''
			return new Date(dateString).toLocaleString('en-US', {
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			})
		}
	},

	beforeUnmount() {
		if (this.pollInterval) {
			clearInterval(this.pollInterval)
		}
	}
}
</script>

<style scoped>
.job-header-section {
	margin-bottom: 24px;
}

.loading-section, .no-job {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	text-align: center;
}

.job-display {
	width: 100%;
}

.job-card {
	border: 1px solid #e0e0e0;
	border-radius: 12px;
	padding: 24px;
	background: white;
	transition: all 0.2s ease;
}

.job-card.running {
	border-left: 4px solid #ff9800;
	background: #fff8e1;
}

.job-card.has-errors {
	border-left: 4px solid #f44336;
}

.job-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 12px;
}

.job-info {
	flex: 1;
}

.job-title {
	font-weight: 600;
	font-size: 16px;
	color: #333;
	margin-bottom: 4px;
}

.job-meta {
	display: flex;
	gap: 16px;
	font-size: 12px;
	color: #666;
}

.job-id {
	font-family: monospace;
}

.job-details {
	border-top: 1px solid #f0f0f0;
	padding-top: 12px;
}

.job-progress {
	margin-bottom: 12px;
}

.job-summary {
	margin-bottom: 8px;
}

.summary-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
	gap: 16px;
	margin-top: 12px;
}

.summary-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 16px;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	background: #fafafa;
	transition: all 0.2s ease;
}

.summary-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-card.success {
	border-color: #4caf50;
	background: #e8f5e8;
}

.summary-card.error {
	border-color: #f44336;
	background: #ffebee;
}

.summary-number {
	font-size: 24px;
	font-weight: 700;
	color: #333;
	margin-bottom: 4px;
}

.summary-card.success .summary-number {
	color: #2e7d32;
}

.summary-card.error .summary-number {
	color: #d32f2f;
}

.summary-label {
	font-size: 12px;
	color: #666;
	text-transform: uppercase;
	font-weight: 500;
	text-align: center;
}

.job-message {
	font-style: italic;
}

@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}

.mdi-spin {
	animation: spin 1s linear infinite;
}
</style>
